import React, { useEffect, useState } from "react";
import {
  Users,        // total users
  Briefcase,    // business users
  FileText,     // jobs
  Grid,         // categories
  UserX,        // blocked users
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";
import axios from "axios";
import { format } from "date-fns";
import config from "../config";

const Dashboard = () => {
  const [stats, setStats] = useState({
    userCount: 0,
    businessUserCount: 0,
    blockedUserCount: 0,
    jobsCount: 0,
    categoriesCount: 0,
    userSignupsMonthly: [],
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${config.BASE_URL}/stats`);
        if (res.data.success) {
          setStats(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch dashboard stats", err);
      }
    };

    fetchStats();
  }, []);

  // map: monthString -> count
  const signupMap = stats.userSignupsMonthly.reduce((acc, entry) => {
    acc[entry.month] = parseInt(entry.count);
    return acc;
  }, {});

  // Jan → Dec of current year
  const currentYear = new Date().getFullYear();
  const months = Array.from({ length: 12 }).map((_, i) => {
    const d = new Date(currentYear, i, 1);
    const monthKey = format(d, "yyyy-MM");
    return {
      name: format(d, "MMM"), // Jan, Feb...
      value: signupMap[monthKey] || 0,
      color: "#000000",
    };
  });

  const totalNewUsers = months.reduce((sum, d) => sum + d.value, 0);

  const statCards = [
    { label: "Total Users", value: stats.userCount, icon: Users },
    { label: "Business Users", value: stats.businessUserCount, icon: Briefcase },
    { label: "Jobs", value: stats.jobsCount, icon: FileText },
    { label: "Categories", value: stats.categoriesCount, icon: Grid },
    { label: "Blocked Accounts", value: stats.blockedUserCount, icon: UserX },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-8 space-y-8">
      {/* Stat Cards */}
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        {statCards.map(({ label, value, icon: Icon }) => (
          <div
            key={label}
            className="flex items-center gap-4 rounded-[6px] bg-white p-4 shadow hover:shadow-md transition-shadow"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#000000] text-white">
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-lg text-gray-300">{label}</p>
              <p className="text-2xl font-semibold">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* User Signups Chart */}
      <div className="rounded-[6px] bg-white p-6 shadow">
        <p className="mb-1 text-sm font-medium text-gray-500">
        User List
        </p>
        <div className="mb-4 text-3xl font-extrabold text-black-500">
          {totalNewUsers}
        </div>
        <div className="h-80 w-full">
          <ResponsiveContainer>
            <BarChart data={months} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#6b7280" }}
              />
              <YAxis hide />
              <Tooltip cursor={{ fill: "transparent" }} />
              <ReferenceLine
                y={Math.max(...months.map((d) => d.value))}
                stroke="#00000"
                strokeDasharray="4 4"
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {months.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
